<script setup lang="ts">
import { VueFinalModal } from 'vue-final-modal';
import { isMobile } from '@/utils/is-mobile';
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';

interface Props {
  cancelable?: boolean;
  swipeToClose?: boolean;
  centered?: boolean;
  zIndex?: number;
}

interface Emits {
  close: [];
}

interface BeforeCloseEvent {
  stop: () => void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const modalOpen = ref(true);
const content = useTemplateRef<HTMLElement>('content');
const focusTrapOptions = { fallbackFocus: () => content.value ?? document.body };
let previouslyFocused: HTMLElement | null = null;
let closeEmitted = false;

onMounted(async () => {
  previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  await nextTick();
  content.value?.focus();
});

onBeforeUnmount(() => previouslyFocused?.focus());

function closeModal() {
  if (!props.cancelable) {
    return;
  }
  modalOpen.value = false;
}

function onBeforeClose(event: BeforeCloseEvent) {
  if (!props.cancelable) {
    event.stop();
    return;
  }
  modalOpen.value = false;
  if (closeEmitted) return;
  closeEmitted = true;
  emit('close');
}
</script>

<template>
  <VueFinalModal
    :model-value="modalOpen"
    :z-index-fn="zIndex === undefined ? undefined : () => zIndex"
    :content-transition="isMobile && !centered ? 'vfm-slide-down' : 'vfm-fade'"
    overlay-transition="vfm-fade"
    overlay-style="background-color: rgba(0, 0, 0, 0.8);"
    :click-to-close="cancelable"
    :esc-to-close="cancelable"
    :focus-trap="focusTrapOptions"
    :swipe-to-close="swipeToClose ? 'down' : 'none'"
    class="flex items-center sm:flex-row sm:justify-center"
    :class="[centered ? 'flex-row justify-center' : 'flex-col justify-end']"
    @before-close="onBeforeClose"
  >
    <div
      ref="content"
      aria-label="Dialog"
      aria-modal="true"
      role="dialog"
      tabindex="-1"
    >
      <slot />
    </div>
  </VueFinalModal>
</template>

<style scoped></style>
